// Resolves the current viewer's Learning Center scope from the working rooftop + user: the
// tier (the two-valued view of the Valet operational model, from the portal products), enabled
// product features (the held portal products, plus appointments from effective_config), and
// visible audiences ('dealer', plus 'driver' if the user is also a driver). Shared by the
// library list and the single-module reader so both scope modules identically.
//
// `scope` is null only until the rooftop store hydrates (nothing should render as in-scope
// before then). `ready` is false while the rooftop store is hydrating or the portal products
// are still loading (an expected multi-second window on a fresh tab — consumers must treat it
// as loading, not as an empty catalog), true once both are known. A customer whose model does
// not resolve still yields a scope (with tier=null), so tier-agnostic modules stay visible
// rather than the whole catalog blanking out (see filterForRooftop).

import { useMemo } from 'react';
import { usePortalProductsState, useProductsLoaded, useRooftop, useUser } from '@store';
import { useCurrentTier } from '@hooks/useCurrentTier';
import { enabledProductFeatures } from '../utils/productFeatures';
import type { DealerScope } from '../utils/resolveModules';
import type { Audience, ModuleFeature } from '../types';

export function useDealerScope(): { scope: DealerScope | null; ready: boolean } {
  const rooftop = useRooftop();
  const user = useUser();
  const products = usePortalProductsState();

  // Null until the products resolve a model; filterForRooftop reads null as "show tier-agnostic
  // modules only".
  const tier = useCurrentTier();
  const productsLoaded = useProductsLoaded();

  const enabledFeatures = useMemo<ModuleFeature[]>(
    () => enabledProductFeatures(products?.entries, rooftop?.effective_config),
    [products, rooftop],
  );

  const isDriver = useMemo(() => {
    const roles = user?.claims?.['x-hasura-allowed-roles'] ?? [];
    return Array.isArray(roles) && roles.includes('driver');
  }, [user]);

  // Keyed on the ROOFTOP being present, not on a resolved tier: once a rooftop is known we can
  // scope (a null tier just narrows to tier-agnostic modules via filterForRooftop). This keeps a
  // customer with an unresolved model from rendering as an empty catalog.
  const scope = useMemo<DealerScope | null>(() => {
    if (!rooftop) return null;
    const audiences: Audience[] = isDriver ? ['dealer', 'driver'] : ['dealer'];
    return { tier, enabledFeatures, audiences };
  }, [rooftop, tier, enabledFeatures, isDriver]);

  return { scope, ready: Boolean(rooftop) && productsLoaded };
}
